import acerto from '@acerto/web-sdk';
import { datadogRum } from '@datadog/browser-rum';
import { reactive } from 'vue';
import { type FeatureFlagName } from '@/constants/featureFlags';
import JwtAuthToken from '@/models/JwtAuthToken';
import { acertoLogoutSessao } from '@/modules/events/acerto';
import { reassinarPushSilenciosamente, removerAssinaturaPush } from '@shared/pwa/utils/pushSubscription';
import adoptlyService from './AdoptlyService';
import { getDividasOrderingService } from './DividasOrderingService';
import dividasSyncService from './DividasSyncService';
import storageService from './StorageService';

const sessionService = {
  token: null as JwtAuthToken | null,
  get isLoggedIn() {
    return !!this.token;
  },

  get is2faAuthorized() {
    return !!this.token?.payload.otpAutenticado;
  },

  get userIsRegistered() {
    return Boolean(this.token?.payload.dataPrimeiroAcesso);
  },

  isFeatureFlagOn(featureFlagName: FeatureFlagName): boolean {
    const featureFlagsJson = this.token?.payload?.featureFlags;
    if (!featureFlagsJson) {
      return false;
    }

    try {
      const featureFlags = JSON.parse(featureFlagsJson) as Record<string, unknown>;
      return featureFlags[featureFlagName] === true;
    } catch {
      return false;
    }
  },

  /**
   * Retorna o valor bruto da flag no JWT (boolean, string de variante ou number de experimento).
   * Valores vêm do GrowthBook, avaliados no login pelo portal e publicados no claim `featureFlags`.
   */
  getFeatureFlagValue(featureFlagName: FeatureFlagName): unknown {
    const featureFlagsJson = this.token?.payload?.featureFlags;
    if (!featureFlagsJson) {
      return undefined;
    }

    try {
      const featureFlags = JSON.parse(featureFlagsJson) as Record<string, unknown>;
      return featureFlags[featureFlagName];
    } catch {
      return undefined;
    }
  },

  login(token: JwtAuthToken) {
    const previousUserHash = this.token?.payload.hash;
    const newUserHash = token.payload.hash;
    if (previousUserHash && previousUserHash !== newUserHash) {
      this.clearPreviousUserCache();
    }
    this.token = token;
    storageService.session.set(token);
    if (token) {
      storageService.sessionSS.set(token);
    }
    this.setupUserTracking(token);
    adoptlyService.init(token);

    // login() também roda a cada minuto via reload(); reassinar só quando a sessão começa ou o
    // usuário muda. Sessão de back-office é um operador no próprio computador, que não deve
    // receber os avisos do consumidor: a assinatura deixada por uma sessão de consumidor que
    // expirou é removida.
    const sessaoComecouOuUsuarioMudou = previousUserHash !== newUserHash;
    if (sessaoComecouOuUsuarioMudou) {
      void (token.isBackOffice ? removerAssinaturaPush() : reassinarPushSilenciosamente());
    }
  },

  /**
   * Limpa o cache do usuário anterior quando há mudança de usuário
   */
  clearPreviousUserCache() {
    void removerAssinaturaPush();
    try {
      dividasSyncService.limparEstadoSessao();
      storageService.vueQueryCache.remove();
      storageService.exibiuLoadingPosLogin.remove();
      storageService.exibiuToastVencimentoMeusAcordos.remove();
      storageService.giftCardModalJtiExibido.remove();
      storageService.checkoutUnicaDividaElegivel.remove();
      storageService.deeplinkCaptacao.remove();
      const dividasOrderingService = getDividasOrderingService();
      dividasOrderingService.limparTodasAsOrdens();
      dividasOrderingService.reinicializarParaNovoUsuario();
      datadogRum.addAction('sys:session_service:previous_user_cache_cleared');
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('Erro ao limpar cache do usuário anterior:', error);
    }
  },

  setupUserTracking(token: JwtAuthToken) {
    const userId = token.payload.hash;
    const sessionId = acerto.getSessionId();

    acerto.setUserId(userId);

    datadogRum.setUser({
      id: userId,
      versaoTermoUso: token.payload.versao_termo_uso,
      versaoPoliticaPrivacidade: token.payload.versao_politica_privacidade,
      timezoneOffset: new Date().getTimezoneOffset(),
      ...this.getTokenMetadata(),
    });

    if (sessionId && typeof clarity === 'function') clarity('identify', userId, sessionId);
  },

  logout(origem: string | null, removeHashFromStorage = true) {
    if (origem != null) {
      acertoLogoutSessao(origem);
    }
    // O reload() de toda navegação chama logout() mesmo sem sessão; sem essa checagem, uma
    // assinatura mantida no navegador por falha na remoção geraria um DELETE a cada navegação.
    if (this.token) {
      void removerAssinaturaPush();
    }
    storageService.session.remove();
    storageService.sessionSS.remove();
    this.token = null;
    dividasSyncService.limparEstadoSessao();

    if (removeHashFromStorage) {
      storageService.hash.remove();
    }
    storageService.vueQueryCache.remove();
    storageService.exibiuLoadingPosLogin.remove();
    storageService.exibiuToastVencimentoMeusAcordos.remove();
    storageService.giftCardModalJtiExibido.remove();
    storageService.checkoutUnicaDividaElegivel.remove();
    storageService.deeplinkCaptacao.remove();
    try {
      const dividasOrderingService = getDividasOrderingService();
      dividasOrderingService.limparTodasAsOrdens();
      dividasOrderingService.reinicializarParaNovoUsuario();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('Erro ao limpar ordens no logout:', error);
    }

    acerto.setUserId(undefined);
    datadogRum.clearUser();
  },

  reload() {
    const token = storageService.session.get();

    if (!token) {
      this.logout(null, false);
      return;
    }

    if (token.isExpired) {
      this.logout('reload_token_expirado', false);
      return;
    }

    this.login(token);
  },

  getTokenMetadata() {
    const session = storageService.session.get();
    const jwtPayload = session?.payload as JwtAuthToken['payload'];
    if (!this.token?.payload && !jwtPayload?.hash) {
      return undefined;
    }

    if (!this.token?.payload) {
      datadogRum.addAction('sys:session_service:tokenEscopoUndefined', {
        origem: jwtPayload.origem,
        utmCampaign: jwtPayload.utmCampaign,
      });
    }

    const { exp, nbf, jti, hash } = this.token?.payload ?? jwtPayload;

    return {
      jti,
      exp,
      nbf,
      hash,
    };
  },
};

export default reactive(sessionService);
